import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';

// Import des composants
import CompanyList from '../../components/company/company-list';
import CompanyAddForm from '../../components/company/company-add-form';
import CompanyEditForm from '../../components/company/company-edit-form';
import CompanyDetails from '../../components/company/company-details';
import CompanyDetailView from '../../components/company/company-detail-view';
import CompanyCollaborators from '../../components/company/company-collaborators';
import CompanyDeleteModal from '../../components/company/company-delete-modal';
import companyStore from "../../store/company-store";
import companyViewModel from "../../view-model/professional/company/company-view-model";
import { useSubscriptionLimitContext } from '../../components/subscription/subscription-limit-provider';

interface CompanyPageProps {}

const CompanyPage: React.FC<CompanyPageProps> = observer(() => {
  const { t } = useTranslation();
  const vm = companyViewModel;
  const { checkCompanyLimit } = useSubscriptionLimitContext();

  // Handler qui vérifie la limite avant d'ouvrir le modal d'ajout
  const handleAddCompanyWithCheck = async () => {
    const canCreate = await checkCompanyLimit();
    if (canCreate) {
      vm.handleAddCompany();
    }
    // Si la limite est atteinte, le modal de limite s'affiche automatiquement
  };

  // Rendu conditionnel en fonction de la vue actuelle
  const renderContent = () => {
    switch (vm.currentView) {
      case 'list':
        return (
          <CompanyList
            onAdd={handleAddCompanyWithCheck}
            onEdit={vm.handleEditCompany}
            onDelete={vm.handleDeleteCompany}
            onView={vm.handleViewCompany}
            onViewCollaborators={vm.handleViewCollaborators}
          />
        );

      case 'add':
      case 'edit':
        return null; // Les formulaires sont maintenant affichés comme modaux ci-dessous

      case 'details':
        return (
          <CompanyDetails
            company={companyStore.getCompanyById || null}
            onEdit={() => vm.setCurrentView('edit')}
            onViewCollaborators={() => vm.setCurrentView('collaborators')}
          />
        );

      case 'view':
        return null; // La vue est maintenant affichée comme modal ci-dessous

      case 'collaborators':
        return (
          <CompanyCollaborators
            companyId={companyStore.getCompanyById?.id || ''}
            companyName={companyStore.getCompanyById?.businessName || ''}
            onBack={vm.handleBackToList}
          />
        );

      default:
        return <div>Vue non reconnue</div>;
    }
  };

  return (
    <div className="">
      <div className="flex flex-col space-y-4 mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            {t('company.page.title', 'Gestion des entreprises')}
          </h1>
        </div>
      </div>

      {renderContent()}

      {/* Modal d'ajout d'entreprise */}
      <CompanyAddForm
        isOpen={vm.currentView === 'add'}
        onClose={vm.handleFormCancel}
      />

      {/* Modal d'édition d'entreprise */}
      <CompanyEditForm
        isOpen={vm.currentView === 'edit'}
        onSubmit={vm.handleFormUpdateSubmit}
        onClose={vm.handleFormCancel}
      />

      {/* Modal de visualisation d'entreprise */}
      <CompanyDetailView
        isOpen={vm.currentView === 'view'}
        company={companyStore.getCompanyById || undefined}
        onClose={vm.handleBackToList}
        onEdit={() => vm.setCurrentView('edit')}
        onViewCollaborators={() => vm.setCurrentView('collaborators')}
      />

      {/* Modal de confirmation de suppression */}
      {vm.isDeleteModalOpen && (
        <CompanyDeleteModal
          companyName={companyStore.getCompanyById?.businessName || ''}
          onConfirm={vm.handleConfirmDelete}
          onCancel={vm.handleCancelDelete}
        />
      )}


    </div>
  );
});

export default CompanyPage;